import { CheckCircle2, RefreshCw, ShieldAlert, XCircle } from 'lucide-react'
import { useId } from 'react'

import { Button, TextField } from '@krp/ui-kit'

import { QueryState } from '@krp/sale-ui'
import { ReasonField } from '@krp/sale-ui'
import '../../../shared/ui/aqsi.css'
import type { ResolveActions, ResolveVm } from '../model/types'
import '@krp/sale-ui/sale.css'

/**
 * Ручная сверка операции с неизвестным исходом (решение владельца
 * 07.10.2026): человек с правом `operations.reconcile` проверяет терминал,
 * кассу и кабинет и записывает итог с причиной. Кто сверял — служба берёт из
 * токена. Ни POST, ни GET к кассе служба при этом не делает; повторить
 * оплату отсюда нельзя — такой кнопки нет.
 */
export function ResolveScreen({ vm, actions }: { vm: ResolveVm; actions: ResolveActions }) {
  const name = useId()
  const draft = vm.draft
  const locked = !vm.allowed || draft.phase !== 'editing'
  return (
    <section className="aqsi-screen" aria-labelledby="aqsi-resolve-title">
      <header className="aqsi-toolbar">
        <h2 id="aqsi-resolve-title">Ручная сверка</h2>
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.checkStatus}>
          Проверить статус
        </Button>
      </header>
      <QueryState query={vm.query} what="Операция" emptyText="Операция не найдена" onRetry={actions.checkStatus} />
      {vm.query.status === 'ready' && (
        <>
          <section className="aqsi-panel" data-tone={vm.allowed ? 'warning' : 'neutral'} aria-label="Операция">
            <h3>
              <ShieldAlert size={16} aria-hidden="true" />
              {vm.kindText}: {vm.stateText}
            </h3>
            {vm.details.map((line) => (
              <p key={line} className="aqsi-card__text">
                {line}
              </p>
            ))}
            <p className="aqsi-card__text">Операция {vm.operationId}</p>
            {vm.allowedReason !== null && <p className="aqsi-card__text">{vm.allowedReason}</p>}
            {vm.resolution !== null && (
              <p className="aqsi-card__text">
                Сверил {vm.resolution.resolvedBy}: {vm.resolution.outcome}. {vm.resolution.reason}. Проверено:{' '}
                {vm.resolution.checked}
              </p>
            )}
          </section>

          {vm.allowed && (
            <form
              className="aqsi-form"
              aria-label="Итог сверки"
              onSubmit={(event) => {
                event.preventDefault()
                actions.submit()
              }}
            >
              <fieldset className="sale__kind" disabled={locked}>
                <legend>Что показала проверка</legend>
                <label>
                  <input
                    type="radio"
                    name={name}
                    checked={draft.outcome === 'Succeeded'}
                    onChange={() => actions.outcomeChanged('Succeeded')}
                  />
                  <CheckCircle2 size={14} aria-hidden="true" /> Операция исполнена
                </label>
                <label>
                  <input
                    type="radio"
                    name={name}
                    checked={draft.outcome === 'Failed'}
                    onChange={() => actions.outcomeChanged('Failed')}
                  />
                  <XCircle size={14} aria-hidden="true" /> Не исполнена — денег/чека нет
                </label>
              </fieldset>
              <ReasonField label="Причина" value={draft.reason} onChange={actions.reasonChanged} />
              <ReasonField
                label="Что проверено на кассе, терминале или в кабинете"
                value={draft.checked}
                onChange={actions.checkedChanged}
              />
              <TextField
                label={vm.resultIdRequired ? 'Номер слипа (для исполненного списания — обязательно)' : 'Номер чека (если виден)'}
                value={draft.resultId}
                onChange={actions.resultIdChanged}
              />
              {vm.submitBlockedReason !== null && <p className="aqsi-card__text">{vm.submitBlockedReason}</p>}
              <div className="aqsi-card__actions">
                <Button type="submit" variant="primary" disabled={!vm.canSubmit} busy={draft.phase === 'sending'}>
                  Записать сверку
                </Button>
                <Button variant="ghost" onClick={actions.close}>
                  {draft.phase === 'done' ? 'Готово' : 'Отмена'}
                </Button>
              </div>
            </form>
          )}
          {!vm.allowed && (
            <div className="aqsi-card__actions">
              <Button variant="ghost" onClick={actions.close}>
                Закрыть
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  )
}
